// "Puntos por partido": points a participant earns per closed group-stage match.
// Mirrors App\Services\Standings\MatchScoringConfig in reva-backend.

export const MATCH_SCORING_PRESET_LABELS = {
  classic: "matchScoring.preset.classic",
  volley: "matchScoring.preset.volley",
  win2_loss1_wo0: "matchScoring.preset.win2Loss1Wo0",
  custom: "matchScoring.preset.custom",
};

export const POINT_FIELDS = ["win", "tie", "loss", "walkover_win", "walkover_loss"];

export const SET_MARGIN_FIELDS = ["win_wide", "win_close", "loss_close", "loss_wide"];

export const MAX_POINTS = 100;

export const DEFAULT_SET_MARGIN = {
  win_close: 2,
  win_wide: 3,
  loss_close: 1,
  loss_wide: 0,
};

export const DEFAULT_MATCH_SCORING = {
  preset: "classic",
  win: 3,
  tie: 1,
  loss: 0,
  walkover_win: 3,
  walkover_loss: 0,
  by_set_margin: null,
};

const toPoints = (value) => {
  const number = Number.parseInt(value, 10);
  if (Number.isNaN(number)) return 0;
  return Math.min(Math.max(number, 0), MAX_POINTS);
};

export const normalizeMatchScoring = (config) => {
  const source = config || DEFAULT_MATCH_SCORING;
  const normalized = { preset: source.preset || "custom" };

  POINT_FIELDS.forEach((field) => {
    normalized[field] = toPoints(source[field]);
  });

  normalized.by_set_margin = source.by_set_margin
    ? SET_MARGIN_FIELDS.reduce(
        (margin, field) => ({
          ...margin,
          [field]: toPoints(source.by_set_margin[field]),
        }),
        {},
      )
    : null;

  return normalized;
};

// Any manual edit turns the config into a custom one, unless it still matches the preset exactly.
export const updateMatchScoringField = (config, field, value, presets = []) => {
  const next = normalizeMatchScoring({ ...config, [field]: value });
  return { ...next, preset: matchingPresetKey(next, presets) };
};

export const updateSetMarginField = (config, field, value, presets = []) => {
  const next = normalizeMatchScoring({
    ...config,
    by_set_margin: { ...config.by_set_margin, [field]: value },
  });
  return { ...next, preset: matchingPresetKey(next, presets) };
};

export const toggleSetMargin = (config, enabled, presets = []) => {
  const next = normalizeMatchScoring({
    ...config,
    by_set_margin: enabled ? DEFAULT_SET_MARGIN : null,
  });
  return { ...next, preset: matchingPresetKey(next, presets) };
};

const sameValues = (a, b) =>
  POINT_FIELDS.every((field) => a[field] === b[field]) &&
  (a.by_set_margin === null || b.by_set_margin === null
    ? a.by_set_margin === b.by_set_margin
    : SET_MARGIN_FIELDS.every((field) => a.by_set_margin[field] === b.by_set_margin[field]));

export const matchingPresetKey = (config, presets = []) => {
  const preset = presets.find((candidate) => sameValues(normalizeMatchScoring(candidate), config));
  return preset ? preset.preset : "custom";
};

// Rows shown in the editor preview: { id, points, tone, badgeId? }, where id is a locale key.
// `points` is a number, or { win, loss } for matches closed without a score.
// `tone` drives the row color: "win", "decidingLoss", "neutral" or "walkoverLoss".
export const matchScoringPreview = (config) => {
  const scoring = normalizeMatchScoring(config);
  const margin = scoring.by_set_margin;
  const rows = margin
    ? [
        { id: "matchScoring.preview.winWide", points: margin.win_wide, tone: "win" },
        { id: "matchScoring.preview.winClose", points: margin.win_close, tone: "win" },
        {
          id: "matchScoring.preview.lossClose",
          points: margin.loss_close,
          tone: margin.loss_close > 0 ? "decidingLoss" : "neutral",
          badgeId: margin.loss_close > 0 ? "matchScoring.decidingSetPoint" : undefined,
        },
        { id: "matchScoring.preview.lossWide", points: margin.loss_wide, tone: "neutral" },
        { id: "matchScoring.preview.noScore", points: { win: scoring.win, loss: scoring.loss }, tone: "neutral" },
      ]
    : [
        { id: "matchScoring.preview.win", points: scoring.win, tone: "win" },
        { id: "matchScoring.preview.loss", points: scoring.loss, tone: "neutral" },
      ];

  return [
    ...rows,
    { id: "matchScoring.preview.tie", points: scoring.tie, tone: "neutral" },
    { id: "matchScoring.preview.walkoverWin", points: scoring.walkover_win, tone: "win" },
    { id: "matchScoring.preview.walkoverLoss", points: scoring.walkover_loss, tone: "walkoverLoss" },
  ];
};

// Short legend for standings tables, e.g. "V 2 · D 1 · E 1 · W.O. 2/0".
export const matchScoringLegendParts = (config) => {
  if (!config) return [];
  const scoring = normalizeMatchScoring(config);

  const results = scoring.by_set_margin
    ? [
        {
          id: "matchScoring.legend.winWide",
          value: scoring.by_set_margin.win_wide,
        },
        {
          id: "matchScoring.legend.winClose",
          value: scoring.by_set_margin.win_close,
        },
        {
          id: "matchScoring.legend.lossClose",
          value: scoring.by_set_margin.loss_close,
        },
        {
          id: "matchScoring.legend.lossWide",
          value: scoring.by_set_margin.loss_wide,
        },
      ]
    : [
        { id: "matchScoring.legend.win", value: scoring.win },
        { id: "matchScoring.legend.loss", value: scoring.loss },
      ];

  return [
    ...results,
    { id: "matchScoring.legend.tie", value: scoring.tie },
    {
      id: "matchScoring.legend.walkover",
      value: `${scoring.walkover_win}/${scoring.walkover_loss}`,
    },
  ];
};
